import { Head, usePage } from "@inertiajs/react"
import { PageProps } from "@/types"
import NavigationBar from "@/components/molecules/NavigationBar/NavigationBar"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import Footer from "@/components/molecules/Footer/Footer"
import { useEffect, useRef, useState } from "react"
import CookieConsentBanner from "@/components/molecules/CookieConsentBanner/CookieConsentBanner"

import ReactGA from "react-ga4"

export function Layout({
    isAuthPage,
    title,
    children,
}: React.PropsWithChildren & { isAuthPage?: boolean; title?: string }) {
    const {
        props: { auth },
        component,
        url,
    } = usePage<PageProps>()
    const [queryClient] = useState(() => new QueryClient())

    const consent = useRef(localStorage.getItem("cookieConsent"))
    const [cookieConsent, setCookieConsent] = useState(
        consent.current === "accepted"
    )
    const [showBanner, setShowBanner] = useState(!consent.current)
    const [ga4Initialised, setGa4Initialised] = useState(false)

    useEffect(() => {
        if (!cookieConsent || isAuthPage) return

        //HubSpot Tracking Cookie
        if (!document.getElementById("hs-script-loader")) {
            const script = document.createElement("script")
            script.type = "text/javascript"
            script.id = "hs-script-loader"
            script.async = true
            script.defer = true
            script.src = "//js.hs-scripts.com/49347929.js"

            document.body.appendChild(script)
        }

        //Google Analytics
        if (!ga4Initialised) {
            const gtagId = import.meta.env.VITE_GOOGLE_ANALYTICS_ID as
                | string
                | undefined
                | null
            if (gtagId?.length) {
                ReactGA.initialize(gtagId)
                setGa4Initialised(true)
            }
        }

        //AHRefs
        if (!document.getElementById("ahrefs-script")) {
            const ahrefsScript = document.createElement("script")
            ahrefsScript.id = "ahrefs-script"
            ahrefsScript.async = true
            ahrefsScript.src = "https://analytics.ahrefs.com/analytics.js"
            ahrefsScript.setAttribute("data-key", "yzgR3F33QIwZOAiuzl8KYw")
            document.head.appendChild(ahrefsScript)
        }

        return () => {
            const hsScript = document.getElementById("hs-script-loader")
            const gtagScript = document.getElementById("gtag-script")
            const ahrefsScript = document.getElementById("ahrefs-script")
            if (hsScript) hsScript.remove()
            if (gtagScript) gtagScript.remove()
            if (ahrefsScript) ahrefsScript.remove()
        }
    }, [cookieConsent])

    const handleCookieAccept = () => {
        localStorage.setItem("cookieConsent", "accepted")
        setCookieConsent(true)
        setShowBanner(false)
    }

    const handleCookieDecline = () => {
        localStorage.setItem("cookieConsent", "declined")
        setCookieConsent(false)
        setShowBanner(false)
    }

    useEffect(() => {
        const scrollContainer = document.querySelector("body")
        if (scrollContainer) {
            scrollContainer.scrollTop = 0
        }

        if (!cookieConsent) return
        const w = window as Window & {
            _hsq?: any[]
            HubSpotConversations?: { widget: { refresh: () => void } }
        }
        const _hsq = (w._hsq || []) as Array<unknown>
        if (_hsq) {
            _hsq.push(["disableAutoTracking"]) //enabling this breaks inertia links
            _hsq.push(["setPath", url])
            _hsq.push(["trackPageView"])
        }
        if (w.HubSpotConversations?.widget) {
            w.HubSpotConversations.widget.refresh()
        }
    }, [url])

    return (
        <>
            <QueryClientProvider client={queryClient}>
                <CookieConsentBanner
                    show={showBanner}
                    acceptCallback={handleCookieAccept}
                    denyCallback={handleCookieDecline}
                />
                <div
                    className={`
                        relative flex min-h-screen w-full grid-cols-1 flex-col
                        items-center bg-bonza-dark text-bonza-pale
                    `}
                >
                    <NavigationBar
                        user={auth.user}
                        overlap={
                            component === "Home" ||
                            component === "Auth/Register" ||
                            component === "Auth/SignupWithPlan" ||
                            component === "Auth/Login" ||
                            component === "Partner" ||
                            component === "Download" ||
                            component === "BlogPost" ||
                            component === "BlogHome"
                        }
                    />
                    <main className="grid h-full w-full grow tracking-[-3%]">
                        {children}
                    </main>
                    <Footer user={auth.user} />
                </div>
            </QueryClientProvider>
        </>
    )
}
